import { useEffect, useRef } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { gsap } from '../lib/gsapSetup';
import SectionTag from '../components/SectionTag';
import { BRANDS } from '../content/siteContent';
import { useReducedMotion } from '../hooks/useMotionPrefs';

export default function BrandExploration() {
  const ref = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return;
    const ctx = gsap.context(() => {
      BRANDS.forEach((_, i) => {
        gsap.fromTo(
          `.brand-img-${i}`,
          { scale: 1.18 },
          {
            scale: 1,
            duration: 1.8,
            ease: 'power2.out',
            scrollTrigger: { trigger: `.brand-panel-${i}`, start: 'top 75%', toggleActions: 'play none none reverse' },
          }
        );
        gsap.fromTo(
          `.brand-info-${i}`,
          { xPercent: 8, opacity: 0 },
          {
            xPercent: 0,
            opacity: 1,
            duration: 0.9,
            ease: 'power3.out',
            scrollTrigger: { trigger: `.brand-panel-${i}`, start: 'top 70%', toggleActions: 'play none none reverse' },
          }
        );
      });
    }, ref);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <section
      ref={ref}
      id="brands"
      data-testid="brand-exploration-section"
      className="relative bg-[#000000] py-24 md:py-32"
    >
      <div className="mb-10 px-5 md:mb-14 md:px-12">
        <SectionTag number="06" label="Partner Brands" />
      </div>

      {BRANDS.map((brand, i) => (
        <div
          key={brand.id}
          className={`brand-panel-${i} relative border-t border-white/10 bg-[#000000]`}
          data-testid={`brand-panel-${brand.id}`}
          data-cursor="EXPLORE"
        >
          <div className="grid grid-cols-1 md:grid-cols-12">
            {/* Visual — ~70% of viewport on desktop */}
            <div className="relative h-[50vh] overflow-hidden md:col-span-8 md:h-[85vh]">
              <img
                src={brand.image}
                alt={brand.name}
                loading="lazy"
                decoding="async"
                className={`brand-img-${i} h-full w-full object-cover will-change-transform`}
              />
              <div className="absolute inset-0 bg-black/35" />
              <span
                className="absolute bottom-6 left-5 font-display text-[10vw] font-semibold uppercase leading-none text-white/10 md:left-12 md:text-8xl"
                aria-hidden="true"
              >
                {String(i + 1).padStart(2, '0')}
              </span>
            </div>

            {/* Info */}
            <div className={`brand-info-${i} relative flex flex-col justify-center gap-5 px-5 py-8 md:col-span-4 md:px-12`}>
              <span
                className={`w-fit border px-3 py-1 text-[10px] uppercase tracking-[0.25em] ${
                  brand.status === 'active'
                    ? 'border-[#47a0d9]/60 text-[#47a0d9]'
                    : 'border-white/25 text-white/50'
                }`}
              >
                {brand.status === 'active' ? 'Active brand' : 'Coming soon'}
              </span>
              <h3 className="font-display text-3xl font-semibold tracking-tight text-white md:text-5xl">
                {brand.name}
              </h3>
              <p className="text-[11px] uppercase tracking-[0.25em] text-white/50">{brand.category}</p>
              <p className="max-w-sm text-sm leading-relaxed text-white/65">{brand.description}</p>
              {brand.url && (
                <a
                  href={brand.url}
                  data-testid={`brand-link-${brand.id}`}
                  className="group mt-2 inline-flex w-fit items-center gap-2 text-[11px] font-medium uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:text-[#47a0d9]"
                >
                  Visit brand <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              )}
            </div>
          </div>
        </div>
      ))}
    </section>
  );
}
